import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { formatPopularity, formatRating, getCategoryName, getRelatedMovies } from "@/utils/movie";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(() => (movie ? getRelatedMovies(movies, movie, 6) : []), [movie]);

  useSeo({
    title: movie
      ? `${movie.title} - 影视详情 - 红桃视频在线观看`
      : "影视详情 - 红桃视频在线观看",
    description: movie
      ? `${movie.title}（${movie.year}年${movie.region}${getCategoryName(movie.category)}），${movie.summary.slice(0, 60)}……`
      : "红桃视频在线观看影视详情页，提供影片资料、剧情简介与相关推荐。",
    keywords: movie
      ? `${movie.title},${movie.title}详情,${getCategoryName(movie.category)},红桃视频在线观看`
      : "影视详情,红桃视频在线观看",
    canonicalPath: `/detail/${id}`,
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}/detail/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("详情页链接已复制，快去分享吧");
    } catch {
      setToast(`请手动复制链接：${url}`);
    }
  };

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-500">
          <i className="ri-film-line text-3xl" />
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-3 text-sm text-foreground-500">
          该影视可能已下架或链接有误，你可以返回首页继续浏览。
        </p>
        <Link
          to="/"
          className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const fav = isFavorite(movie.id);
  const metaRows = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "分类", value: getCategoryName(movie.category) },
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "地区", value: movie.region },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join("、") },
    { label: "热度", value: `${formatPopularity(movie.popularity)} 次播放` },
    { label: "上线时间", value: movie.createdAt },
  ];

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
          首页
        </Link>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-s-line" />
        </span>
        <Link
          to={`/category/${movie.category}`}
          className="cursor-pointer transition-colors hover:text-primary-600"
        >
          {getCategoryName(movie.category)}
        </Link>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-s-line" />
        </span>
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      {/* Main visual */}
      <section className="relative mt-5 overflow-hidden rounded-lg">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} 主视觉`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/60 to-black/30" />
        </div>

        <div className="relative z-10 flex flex-col gap-6 p-4 md:flex-row md:items-end md:p-8">
          <div className="h-56 w-40 shrink-0 overflow-hidden rounded-lg border border-background-50/20 md:h-72 md:w-52">
            <img
              src={movie.poster}
              alt={`${movie.title} 海报`}
              title={`${movie.title} - ${getCategoryName(movie.category)}详情`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-0.5 text-xs font-medium text-background-50">
                {getCategoryName(movie.category)}
              </span>
              {movie.genres.map((g) => (
                <span
                  key={g}
                  className="rounded-md border border-background-50/30 px-2.5 py-0.5 text-xs text-background-100"
                >
                  {g}
                </span>
              ))}
            </div>
            <h1 className="mt-3 font-heading text-3xl text-background-50 md:text-5xl">
              {movie.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
              <span className="flex items-center gap-1 text-lg font-bold text-accent-300">
                <i className="ri-star-fill text-sm" />
                {formatRating(movie.rating)}
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.director} 执导</span>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const added = toggleFavorite(movie.id);
                  setToast(added ? "已加入收藏" : "已取消收藏");
                }}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                  fav
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-background-50/15 text-background-50 hover:bg-background-50/25"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={fav ? "ri-heart-fill" : "ri-heart-line"} />
                </span>
                {fav ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50/15 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/25"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-share-forward-line" />
                </span>
                分享
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Info + summary */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionHeading title="剧情简介" icon="ri-file-text-line" />
          <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{movie.summary}</p>

          <div className="mt-6 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-accent-700">
              <i className="ri-information-line text-lg" />
            </span>
            <p className="text-sm leading-relaxed text-accent-900">
              温馨提示：本站为影视信息展示平台，暂不提供在线播放服务。以上内容仅供影视资料查询与交流参考。
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to={`/category/${movie.category}`}
              className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              所属分类：{getCategoryName(movie.category)}
            </Link>
            {movie.genres.map((g) => (
              <Link
                key={g}
                to={`/search?q=${encodeURIComponent(g)}`}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {g}
              </Link>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-1">
          <SectionHeading title="资料信息" icon="ri-list-check" />
          <dl className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
            {metaRows.map((row, i) => (
              <div
                key={row.label}
                className={`flex gap-4 px-4 py-3 text-sm ${
                  i !== metaRows.length - 1 ? "border-b border-background-200" : ""
                }`}
              >
                <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                <dd className="min-w-0 flex-1 text-foreground-900">{row.value}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      {/* Related */}
      <section className="mt-12">
        <SectionHeading
          title="相关推荐"
          subtitle="根据类型、导演与热度为你精选"
          icon="ri-magic-line"
          moreTo={`/category/${movie.category}`}
          moreLabel={`更多${getCategoryName(movie.category)}`}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {related.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-in rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}